import React from 'react';
import { createPortal } from 'react-dom';
import { useLanguage } from '../contexts/LanguageContext';
import type { Route, Stop } from '../types/ferry';

interface RouteInfoPanelProps {
  route: Route;
  fromStop: Stop;
  toStop: Stop;
  position: { x: number; y: number };
  onClose: () => void;
  onSwap: () => void;
  onApply: (fromId: string, toId: string) => void;
}

export const RouteInfoPanel: React.FC<RouteInfoPanelProps> = ({
  route,
  fromStop,
  toStop,
  position,
  onClose,
  onSwap,
  onApply,
}) => {
  const { language, t } = useLanguage();

  // Get next 3 services for this route direction
  const getNextServices = () => {
    // Use Japan Standard Time (JST = UTC+9)
    const now = new Date();
    const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
    const jstTime = new Date(utc + (3600000 * 9)); // Add 9 hours for JST

    const currentTime = jstTime.getHours() * 60 + jstTime.getMinutes(); // minutes since midnight
    const currentDay = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][jstTime.getDay()];

    const services = [];

    // Iterate through all services in this route
    for (const service of route.services) {
      // Find the correct direction key by checking which direction contains our from->to pattern
      let scheduleKey = '';
      const availableKeys = Object.keys(service.schedules);

      for (const key of availableKeys) {
        // Parse the direction key (e.g., "takamatsu_to_naoshima")
        const keyParts = key.split('_to_');
        if (keyParts.length === 2) {
          const keyFromStop = keyParts[0];
          const keyToStop = keyParts[1];

          // Check if this direction matches our desired travel direction
          // Handle both full stop IDs and shortened forms
          const fromMatches = keyFromStop === fromStop.id ||
                             (fromStop.id === 'naoshima_miyanoura' && keyFromStop === 'naoshima') ||
                             (fromStop.id === 'teshima_ieura' && keyFromStop === 'teshima') ||
                             (fromStop.id === 'shodoshima_tonosho' && keyFromStop === 'shodoshima_tonosho') ||
                             (fromStop.id.includes('_') && keyFromStop === fromStop.id.split('_')[0]);

          const toMatches = keyToStop === toStop.id ||
                           (toStop.id === 'naoshima_miyanoura' && keyToStop === 'naoshima') ||
                           (toStop.id === 'teshima_ieura' && keyToStop === 'teshima') ||
                           (toStop.id === 'shodoshima_tonosho' && keyToStop === 'shodoshima_tonosho') ||
                           (toStop.id.includes('_') && keyToStop === toStop.id.split('_')[0]);

          if (fromMatches && toMatches) {
            scheduleKey = key;
            break;
          }
        }
      }

      const schedules = scheduleKey ? service.schedules[scheduleKey] || [] : [];

      for (const schedule of schedules) {
        // Check if this schedule runs today
        const runsToday = schedule.days.includes('daily') || schedule.days.includes(currentDay);
        if (!runsToday) continue;

        // Parse departure time
        const [hours, minutes] = schedule.departure.split(':').map(Number);
        const departureMinutes = hours * 60 + minutes;

        // Only include future departures
        if (departureMinutes > currentTime) {
          services.push({
            departure: schedule.departure,
            arrival: schedule.arrival,
            serviceName: service.name,
            duration: service.duration,
          });
        }
      }
    }

    // Sort by departure time and return first 3
    services.sort((a, b) => {
      const [aHours, aMinutes] = a.departure.split(':').map(Number);
      const [bHours, bMinutes] = b.departure.split(':').map(Number);
      return (aHours * 60 + aMinutes) - (bHours * 60 + bMinutes);
    });

    return services.slice(0, 3);
  };

  const nextServices = getNextServices();

  return createPortal(
    <>
      {/* Desktop: Floating panel */}
      <div
        className="hidden md:block fixed bg-white rounded-lg shadow-xl border border-gray-200 z-[9999]"
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
          transform: 'translate(-50%, -50%)', // Center on the route
          minWidth: '320px',
          maxWidth: '360px',
        }}
      >
        <div className="p-4">
          {/* Header with close button */}
          <div className="flex items-start justify-between mb-4">
            <h3 className="text-base font-semibold text-gray-600">
              {language === 'zh' ? '航线详情' : 'Route Details'}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* From/To with Swap Button */}
          <div className="mb-4 bg-gray-50 rounded-lg p-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex-1">
                <div className="text-xs text-gray-500 mb-0.5">
                  {language === 'zh' ? '从' : 'From'}
                </div>
                <div className="font-semibold text-gray-900">
                  {t.getStopName(fromStop.name)}
                </div>
              </div>

              <button
                onClick={onSwap}
                className="p-2 rounded-full hover:bg-gray-200 transition-colors"
                aria-label="Swap"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
              </button>

              <div className="flex-1">
                <div className="text-xs text-gray-500 mb-0.5">
                  {language === 'zh' ? '到' : 'To'}
                </div>
                <div className="font-semibold text-gray-900">
                  {t.getStopName(toStop.name)}
                </div>
              </div>
            </div>
          </div>

          {/* Next Services */}
          <div className="mb-4">
            <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">
              {language === 'zh' ? '下一班次' : 'Next Services'}
            </h4>
            <div className="space-y-2">
              {nextServices.length > 0 ? (
                nextServices.map((service, index) => (
                  <div key={index} className="flex items-center justify-between text-sm p-2 bg-blue-50 rounded-md">
                    <div>
                      <div className="font-medium text-gray-900">{service.departure}</div>
                      <div className="text-xs text-gray-500">{service.serviceName}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-gray-600">{service.arrival}</div>
                      <div className="text-xs text-gray-500">{service.duration}min</div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-400 italic">
                  {language === 'zh' ? '今日无后续班次' : 'No more services today'}
                </p>
              )}
            </div>
          </div>

          {/* Apply Button */}
          <button
            onClick={() => onApply(fromStop.id, toStop.id)}
            className="w-full px-4 py-2.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
          >
            {language === 'zh' ? '应用到搜索' : 'Apply to Search'}
          </button>
        </div>
      </div>

      {/* Mobile: Bottom sheet */}
      <div className="md:hidden fixed inset-x-0 bottom-0 bg-white rounded-t-2xl shadow-2xl z-[9999] animate-slide-up">
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-12 h-1 bg-gray-300 rounded-full" />
        </div>

        <div className="p-4 pb-safe">
          {/* Header with close button */}
          <div className="flex items-start justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-600">
              {language === 'zh' ? '航线详情' : 'Route Details'}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* From/To with Swap Button */}
          <div className="mb-4 bg-gray-50 rounded-xl p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1">
                <div className="text-xs text-gray-500 mb-1">
                  {language === 'zh' ? '从' : 'From'}
                </div>
                <div className="font-semibold text-gray-900 text-base">
                  {t.getStopName(fromStop.name)}
                </div>
              </div>

              <button
                onClick={onSwap}
                className="p-3 rounded-full hover:bg-gray-200 transition-colors"
                aria-label="Swap"
              >
                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
              </button>

              <div className="flex-1">
                <div className="text-xs text-gray-500 mb-1">
                  {language === 'zh' ? '到' : 'To'}
                </div>
                <div className="font-semibold text-gray-900 text-base">
                  {t.getStopName(toStop.name)}
                </div>
              </div>
            </div>
          </div>

          {/* Next Services */}
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-gray-500 uppercase mb-3">
              {language === 'zh' ? '下一班次' : 'Next Services'}
            </h4>
            <div className="space-y-3">
              {nextServices.length > 0 ? (
                nextServices.map((service, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                    <div>
                      <div className="font-medium text-gray-900 text-base">{service.departure}</div>
                      <div className="text-sm text-gray-500">{service.serviceName}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-gray-600 text-base">{service.arrival}</div>
                      <div className="text-sm text-gray-500">{service.duration}min</div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-base text-gray-400 italic">
                  {language === 'zh' ? '今日无后续班次' : 'No more services today'}
                </p>
              )}
            </div>
          </div>

          {/* Apply Button */}
          <button
            onClick={() => onApply(fromStop.id, toStop.id)}
            className="w-full px-6 py-3.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-base"
          >
            {language === 'zh' ? '应用到搜索' : 'Apply to Search'}
          </button>
        </div>
      </div>

      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-25 z-[9998]"
        onClick={onClose}
      />
    </>,
    document.body
  );
};
